/**
 * src/app/error.jsx
 * Error boundary global — captura errores de runtime en cualquier ruta
 * y muestra UI branded en lugar del stack trace crudo de Next.js.
 *
 * También loguea el error a /api/audit/client-error para que el admin
 * pueda revisarlo en /master/audit (filter action='CLIENT_ERROR').
 */

'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import * as Sentry from '@sentry/nextjs';
import { Warning, House, ArrowsClockwise, EnvelopeSimple, Scales } from '@phosphor-icons/react/dist/ssr';

export default function GlobalError({ error, reset }) {
  useEffect(() => {
    // Reportar al backend para que quede en audit logs.
    // Best effort — si falla no hacemos nada (ya estamos en error state).
    const correlationId = error?.digest || crypto.randomUUID?.() || `err_${Date.now()}`;
    fetch('/api/audit/client-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: error?.message || 'Unknown error',
        digest: error?.digest || null,
        url: typeof window !== 'undefined' ? window.location.href : null,
        ua: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        correlation_id: correlationId,
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {});

    // También reportamos a Sentry (si está configurado) para que el equipo
    // se entere por email/Slack ANTES de que el cliente reporte. Sentry
    // recibe el stack trace con source maps desofuscados → línea exacta
    // del bug. Si no hay DSN configurada, esto no hace nada.
    Sentry.captureException(error, {
      tags: {
        digest: error?.digest || 'no-digest',
        component: 'GlobalErrorBoundary',
      },
      extra: {
        correlation_id: correlationId,
        url: typeof window !== 'undefined' ? window.location.href : null,
      },
    });
  }, [error]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0f 0%, #18181b 100%)',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
      fontFamily: 'var(--font-manrope), -apple-system, sans-serif',
    }}>
      <div style={{ maxWidth: 540, width: '100%', textAlign: 'center' }}>
        <Link href="/" style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          color: '#fff', textDecoration: 'none', marginBottom: '2.5rem',
          fontFamily: 'var(--font-playfair), serif', fontWeight: 700, fontSize: '1.1rem',
        }}>
          <Scales size={20} color="#D4AF37" weight="fill" />
          Dr. Víctor Legal
        </Link>

        <div style={{
          width: 72, height: 72, margin: '0 auto 1.5rem',
          background: 'rgba(239,68,68,0.12)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Warning size={36} color="#ef4444" weight="fill" />
        </div>

        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.75rem' }}>
          Algo salió mal
        </h1>
        <p style={{ color: '#a1a1aa', fontSize: '1rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
          Encontramos un error inesperado y nuestro equipo ya fue notificado
          automáticamente. Prueba refrescar la página — si el problema persiste,
          escríbenos.
        </p>

        {/* SEC FIX (2026-05): NUNCA mostrar error.stack ni error.message al user.
            Eso filtraba paths del filesystem, nombres de funciones internas,
            y info de debugging que un atacante puede usar para mapear el
            schema. Solo mostramos el digest (correlation ID seguro) que el
            user puede pasar a soporte. El stack lo recibe Sentry server-side
            con source maps desofuscados. */}
        {error?.digest && (
          <div style={{
            display: 'inline-block',
            padding: '0.5rem 0.85rem',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 6,
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            color: '#a1a1aa',
            marginBottom: '2rem',
          }}>
            <span style={{ color: '#d4a72c', fontWeight: 700 }}>Ref:</span> {error.digest}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => reset()} style={ButtonPrimaryStyle}>
            <ArrowsClockwise size={16} weight="bold" /> Reintentar
          </button>
          <Link href="/" style={ButtonSecondaryStyle}>
            <House size={16} weight="fill" /> Inicio
          </Link>
        </div>

        <div style={{
          marginTop: '2.5rem',
          padding: '1rem 1.25rem',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 10,
          fontSize: '0.85rem',
          color: '#a1a1aa',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <EnvelopeSimple size={18} color="#71717a" />
          ¿Necesitas ayuda urgente?{' '}
          <a href="mailto:soporte@drvictorlegal.com" style={{ color: '#D4AF37', textDecoration: 'none' }}>
            soporte@drvictorlegal.com
          </a>
        </div>
      </div>
    </div>
  );
}

const ButtonPrimaryStyle = {
  background: '#D4AF37',
  color: '#0a0a0f',
  border: 'none',
  borderRadius: 10,
  padding: '0.75rem 1.25rem',
  fontWeight: 700,
  fontSize: '0.9rem',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

const ButtonSecondaryStyle = {
  background: 'transparent',
  color: '#a1a1aa',
  border: '1px solid #3f3f46',
  borderRadius: 10,
  padding: '0.75rem 1.25rem',
  fontWeight: 600,
  fontSize: '0.9rem',
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};
